import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { Menu, MoonStar, SunMedium, X, ArrowRight } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { siteConfig } from "../config/siteConfig";
import { BrandLogo } from "./BrandLogo";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const { theme, toggleTheme } = useTheme();

  const loginUrl = process.env.REACT_APP_OOMS_LOGIN_URL || siteConfig.loginUrl;
  const registerUrl = process.env.REACT_APP_OOMS_REGISTER_URL || siteConfig.registerUrl;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "How It Works", path: "/how-it-works" },
    { name: "Industries", path: "/industries" },
    { name: "Pricing", path: "/pricing" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="relative">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background/95 py-3 shadow-lg shadow-primary-600/10 ring-1 ring-border backdrop-blur-xl dark:bg-slate-950/85"
            : "bg-background/90 py-4 shadow-sm shadow-primary-600/5 ring-1 ring-border/70 backdrop-blur-xl dark:bg-slate-950/75"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap sm:flex-nowrap items-center justify-between gap-3">
          <BrandLogo />

          <nav className="hidden lg:flex items-center gap-1 min-w-0 flex-1 justify-center">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`relative px-3 xl:px-4 py-2 rounded-xl font-heading text-[13px] xl:text-sm font-bold tracking-wide transition-all duration-300 text-center leading-tight ${
                  isActive(link.path)
                    ? "text-primary-600 bg-primary-50/80 dark:bg-primary-950/40"
                    : "text-text-sub dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-surface-hover dark:hover:bg-slate-900/60 whitespace-nowrap"
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute bottom-1 left-4 right-4 h-0.5 bg-gradient-to-r from-primary-500 to-cyan-400 rounded-full"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <motion.button
              type="button"
              whileTap={{ scale: 0.92, rotate: 12 }}
              onClick={toggleTheme}
              className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-border bg-background text-text-sub hover:text-primary-600 hover:border-primary-200 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 dark:hover:text-primary-400 transition-all duration-300"
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={theme}
                  initial={{ opacity: 0, rotate: -40, scale: 0.6 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 40, scale: 0.6 }}
                  transition={{ duration: 0.2 }}
                  className="inline-flex"
                >
                  {theme === "dark" ? (
                    <SunMedium className="w-5 h-5 text-amber-400" />
                  ) : (
                    <MoonStar className="w-5 h-5 text-primary-600" />
                  )}
                </motion.span>
              </AnimatePresence>
            </motion.button>
            <a
              href={loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 xl:px-5 py-2.5 rounded-xl font-heading text-sm font-bold text-text-sub border border-border hover:border-primary-400 hover:text-primary-600 transition-all duration-300 cursor-pointer"
            >
              <span>Login</span>
            </a>
            <a
              href={registerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 xl:px-5 py-2.5 rounded-xl font-heading text-sm font-bold bg-gradient-to-r from-primary-600 to-cyan-500 hover:from-primary-700 hover:to-cyan-600 text-white shadow-lg shadow-primary-600/25 transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
            >
              <span>Get Started</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="flex items-center gap-2 lg:hidden shrink-0 ml-auto">
            <motion.button
              type="button"
              whileTap={{ scale: 0.92 }}
              onClick={toggleTheme}
              className="p-2.5 rounded-xl border border-border bg-background text-text-sub shadow-sm hover:bg-surface-hover hover:text-primary-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 transition-all duration-300"
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={theme}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  transition={{ duration: 0.15 }}
                  className="inline-flex"
                >
                  {theme === "dark" ? (
                    <SunMedium className="w-5 h-5 text-amber-400" />
                  ) : (
                    <MoonStar className="w-5 h-5" />
                  )}
                </motion.span>
              </AnimatePresence>
            </motion.button>
            <motion.button
              type="button"
              whileTap={{ scale: 0.92 }}
              onClick={() => setIsOpen(!isOpen)}
              className="p-2.5 rounded-xl border border-border bg-background text-text-sub shadow-sm hover:bg-surface-hover hover:text-primary-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 transition-all duration-300"
              aria-label="Toggle mobile menu"
              aria-expanded={isOpen}
              aria-controls="mobile-navigation"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={isOpen ? "close" : "open"}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.15 }}
                  className="inline-flex"
                >
                  {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </motion.span>
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        <motion.div
          style={{ scaleX: scrollYProgress, transformOrigin: "0% 50%" }}
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 via-cyan-400 to-amber-400 origin-left will-change-transform"
        />
      </header>

      <AnimatePresence>
        {isOpen && (
          <motion.nav
            id="mobile-navigation"
            initial={{ opacity: 0, y: -12, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -12, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden absolute left-0 right-0 top-full z-[60] overflow-hidden border-b border-border bg-background/98 shadow-2xl backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/98"
            aria-label="Mobile navigation"
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 space-y-4">
              <div className="rounded-3xl border border-border bg-surface/90 p-2 shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
                {navLinks.map((link, idx) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.04 * idx }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center justify-between rounded-2xl px-4 py-3.5 font-heading text-sm font-bold transition-all duration-200 ${
                        isActive(link.path)
                          ? "bg-gradient-to-r from-primary-600 to-cyan-500 text-white shadow-lg shadow-primary-600/20"
                          : "text-foreground dark:text-slate-200 hover:bg-surface-hover dark:hover:bg-slate-800"
                      }`}
                    >
                      <span>{link.name}</span>
                      <ArrowRight
                        className={`h-4 w-4 ${isActive(link.path) ? "opacity-90" : "opacity-50"}`}
                      />
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <a
                  href={loginUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-border bg-background px-5 py-3.5 font-heading text-sm font-bold text-foreground transition hover:bg-surface-hover active:scale-[0.99]"
                >
                  Login
                </a>
                <a
                  href={registerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-primary-600 to-cyan-500 px-5 py-3.5 font-heading text-sm font-bold text-white shadow-lg shadow-primary-600/15 transition hover:brightness-110 active:scale-[0.99]"
                >
                  Get Started Free
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </div>
  );
};
